/**
 * One place that answers "can this proof be used?" for every consumer.
 *
 * Status model (2026-09 refinement): a proof is usable when its status is `ready`; `approved` is an
 * accepted alias written before the transition. Archived rows are never usable. "Cleared for" is the
 * `channels_allowed` tag set with the tokens consumers already key on.
 */
import type { ProofMetric } from '../../types/customer-ops.js';
import { isProofExpirationPast } from './proof-metrics-display.js';

export type ProofClearedForKey = 'email' | 'website' | 'sales_deck';

/** Stored `channels_allowed` token for each "Cleared for" tag. Internal use is implicit for every ready row. */
export const PROOF_CLEARED_FOR_TOKENS: Record<ProofClearedForKey, string> = {
  email: 'outreach_email',
  website: 'public_website',
  sales_deck: 'sales_deck',
};

export const PROOF_CLEARED_FOR_LABELS: Record<ProofClearedForKey, string> = {
  email: 'Email',
  website: 'Website',
  sales_deck: 'Sales deck',
};

export const PROOF_CLEARED_FOR_KEYS: readonly ProofClearedForKey[] = ['email', 'website', 'sales_deck'];

type ReadinessRow = Pick<ProofMetric, 'status'> & Partial<Pick<ProofMetric, 'archived_at'>>;
type CurrentRow = ReadinessRow & Pick<ProofMetric, 'expires_at'>;
type ClearedRow = Partial<Pick<ProofMetric, 'channels_allowed'>>;

/** Ready to use internally: status ready (or legacy approved) and not archived. */
export function isProofReady(row: ReadinessRow): boolean {
  if (row.archived_at) return false;
  return row.status === 'ready' || row.status === 'approved';
}

/** Ready and not past its optional expiry (a missing expiry means it never expires). */
export function isProofCurrent(row: CurrentRow): boolean {
  return isProofReady(row) && !isProofExpirationPast(row);
}

export function isProofClearedFor(row: ClearedRow, key: ProofClearedForKey): boolean {
  return (row.channels_allowed ?? []).includes(PROOF_CLEARED_FOR_TOKENS[key]);
}

export function proofClearedForKeys(row: ClearedRow): ProofClearedForKey[] {
  return PROOF_CLEARED_FOR_KEYS.filter((key) => isProofClearedFor(row, key));
}

/** Usable in outbound email (leads composer, prospect nurture, Network Pulse). */
export function isProofEmailReady(row: CurrentRow & ClearedRow & Pick<ProofMetric, 'claim_text'>): boolean {
  return isProofCurrent(row) && isProofClearedFor(row, 'email') && row.claim_text.trim().length > 0;
}

/** Usable on the public website: cleared, and either not a named customer or the customer is fine being named. */
export function isProofWebsiteReady(
  row: CurrentRow & ClearedRow & Pick<ProofMetric, 'anonymization'> & Partial<Pick<ProofMetric, 'named_use_ok'>>,
): boolean {
  return isProofCurrent(row)
    && isProofClearedFor(row, 'website')
    && (row.anonymization !== 'named' || row.named_use_ok === true);
}

/** Human status for the library badge. Never changes what is stored. */
export function proofLibraryStatusLabel(row: CurrentRow): 'Ready' | 'Stale' | 'Draft' | 'Archived' | 'Revoked' {
  if (row.archived_at || row.status === 'archived') return 'Archived';
  if (row.status === 'revoked') return 'Revoked';
  if (row.status === 'ready' || row.status === 'approved' || row.status === 'expired') {
    return isProofExpirationPast(row) || row.status === 'expired' ? 'Stale' : 'Ready';
  }
  return 'Draft';
}
